import { SUPABASE_ANON_KEY, SUPABASE_URL, supabase } from '../supabaseClient';
import { createChallengePhotoThumb } from '../utils/challengePhotoCompress';
import {
  normalizeChallengeContent,
  normalizeChallengeIntro,
  normalizeChallengeThemeColor,
  challengePhotoThumbPath,
  type ChallengeAnswer,
  type ChallengeContent,
  type ChallengeProjectIntro,
} from '../types/assignmentChallenge';

const PHOTO_BUCKET = 'assignment-challenge-photos';
const INFO_CACHE_PREFIX = 'cp.challengePublic.info.v2.';
const SESSION_KEY_PREFIX = 'cp.challengePublic.session.v1.';

export type PublicChallengeItem = {
  id: string;
  title: string;
  content: ChallengeContent;
  thumbnailUrl: string | null;
  sortOrder: number;
  revision: number;
};

export type PublicChallengeInfo = {
  signature: string;
  projectTitle: string;
  themeColor: string;
  intro: ChallengeProjectIntro;
  items: PublicChallengeItem[];
};

export type PublicChallengeStatus = { itemId: string; submittedAt: string; locked: boolean };

export type PublicChallengeSession = {
  session: string;
  memberName: string;
  expiresAt: string;
};

export function formatChallengePublicError(raw: string): string {
  const msg = raw || '';
  if (/link closed|invalid token/i.test(msg)) return '챌린지 링크가 닫혔거나 올바르지 않습니다. 선생님께 새 링크를 받아 주세요.';
  if (/too many attempts/i.test(msg)) return '입장 시도가 너무 많아요. 10분 뒤에 다시 시도해 주세요.';
  if (/name or entry number mismatch/i.test(msg)) return '이름 또는 입장번호가 일치하지 않습니다.';
  if (/entry number ambiguous/i.test(msg)) return '같은 이름·입장번호가 여러 명이에요. 선생님께 문의해 주세요.';
  if (/member not assigned/i.test(msg)) return '이 챌린지에 참여하도록 지정되지 않았어요. 선생님께 문의해 주세요.';
  if (/invalid entry number/i.test(msg)) return '입장번호 6자리를 입력해 주세요.';
  if (/name required/i.test(msg)) return '이름을 입력해 주세요.';
  if (/session expired/i.test(msg)) return '입장 시간이 지났어요. 다시 입장해 주세요.';
  if (/submission locked/i.test(msg)) return '선생님이 확인한 챌린지라 수정할 수 없어요.';
  if (/QUOTA_EXCEEDED/i.test(msg)) return '선생님의 사진 저장 공간이 가득 찼어요. 선생님께 알려 주세요.';
  if (/too many uploads/i.test(msg)) return '사진을 너무 많이 올렸어요. 잠시 뒤 다시 시도해 주세요.';
  if (/photo required/i.test(msg)) return '사진이 필요한 인증 항목이 있어요.';
  if (/text required/i.test(msg)) return '글이 필요한 인증 항목이 있어요.';
  if (/text too long/i.test(msg)) return '글은 2000자까지 쓸 수 있어요.';
  if (/photo (invalid|not uploaded)/i.test(msg)) return '사진 업로드가 끝나지 않았어요. 사진을 다시 선택해 주세요.';
  if (/challenge not found/i.test(msg)) return '챌린지를 찾을 수 없어요. 새로고침해 주세요.';
  if (/Failed to fetch|NetworkError/i.test(msg)) return '네트워크 연결을 확인해 주세요.';
  return msg || '요청에 실패했습니다.';
}

async function postAnonRpc<T>(fn: string, body: Record<string, unknown>): Promise<T> {
  const baseUrl = SUPABASE_URL.replace(/\/$/, '');
  const anonKey = SUPABASE_ANON_KEY.trim();
  if (!baseUrl || !anonKey) throw new Error('Supabase 환경변수가 설정되지 않았습니다.');
  let res: Response;
  try {
    res = await fetch(`${baseUrl}/rest/v1/rpc/${fn}`, {
      method: 'POST',
      headers: {
        apikey: anonKey,
        Authorization: `Bearer ${anonKey}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });
  } catch (e) {
    throw new Error(formatChallengePublicError(e instanceof Error ? e.message : ''));
  }
  const text = await res.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = null;
  }
  if (!res.ok) {
    const err = parsed as { message?: string } | null;
    throw new Error(formatChallengePublicError(err?.message || text || `HTTP ${res.status}`));
  }
  const okFalse = parsed as { ok?: unknown; error?: unknown } | null;
  if (okFalse && typeof okFalse === 'object' && okFalse.ok === false) {
    throw new Error(formatChallengePublicError(String(okFalse.error ?? '')));
  }
  return parsed as T;
}

function mapStatuses(raw: unknown): PublicChallengeStatus[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((r) => {
      const o = (r ?? {}) as Record<string, unknown>;
      const itemId = String(o.item_id ?? '');
      if (!itemId) return null;
      return { itemId, submittedAt: String(o.submitted_at ?? ''), locked: o.locked === true };
    })
    .filter((s): s is PublicChallengeStatus => s != null);
}

/** 캐시 즉시 표시용 */
export function readCachedChallengeInfo(token: string): PublicChallengeInfo | null {
  try {
    const raw = window.localStorage.getItem(`${INFO_CACHE_PREFIX}${token}`);
    return raw ? (JSON.parse(raw) as PublicChallengeInfo) : null;
  } catch {
    return null;
  }
}

/** 서명(signature)이 같으면 서버가 본문을 생략 → 캐시 재사용 */
export async function fetchChallengeInfo(token: string): Promise<PublicChallengeInfo> {
  const cached = readCachedChallengeInfo(token);
  const res = await postAnonRpc<Record<string, unknown>>('challenge_public_info', {
    p_token: token,
    p_known_signature: cached?.signature ?? null,
  });
  if (res?.unchanged === true && cached) return cached;
  const project = (res?.project ?? {}) as Record<string, unknown>;
  const items = (Array.isArray(res?.items) ? res.items : []).map((r) => {
    const o = (r ?? {}) as Record<string, unknown>;
    return {
      id: String(o.id ?? ''),
      title: String(o.title ?? ''),
      content: normalizeChallengeContent(o.content),
      thumbnailUrl: typeof o.thumbnail_url === 'string' && o.thumbnail_url ? o.thumbnail_url : null,
      sortOrder: Number(o.sort_order) || 0,
      revision: Number(o.revision) || 1,
    } satisfies PublicChallengeItem;
  });
  const info: PublicChallengeInfo = {
    signature: String(res?.signature ?? ''),
    projectTitle: String(project.title ?? ''),
    themeColor: normalizeChallengeThemeColor(typeof project.theme_color === 'string' ? project.theme_color : ''),
    intro: normalizeChallengeIntro(
      project.intro && typeof project.intro === 'object' && Object.keys(project.intro).length > 0
        ? project.intro
        : null,
      items[0]?.content
    ),
    items,
  };
  try {
    window.localStorage.setItem(`${INFO_CACHE_PREFIX}${token}`, JSON.stringify(info));
  } catch {
    /* ignore */
  }
  return info;
}

export function readStoredChallengeSession(token: string): PublicChallengeSession | null {
  try {
    const raw = window.localStorage.getItem(`${SESSION_KEY_PREFIX}${token}`);
    if (!raw) return null;
    const s = JSON.parse(raw) as PublicChallengeSession;
    if (!s?.session || Date.parse(s.expiresAt) <= Date.now()) return null;
    return s;
  } catch {
    return null;
  }
}

export function clearStoredChallengeSession(token: string): void {
  try {
    window.localStorage.removeItem(`${SESSION_KEY_PREFIX}${token}`);
  } catch {
    /* ignore */
  }
}

export async function verifyChallengeMember(params: {
  token: string;
  name: string;
  entryNumber: string;
}): Promise<{ session: PublicChallengeSession; statuses: PublicChallengeStatus[] }> {
  const res = await postAnonRpc<Record<string, unknown>>('challenge_public_verify', {
    p_token: params.token,
    p_member_name: params.name,
    p_entry_number: params.entryNumber,
  });
  const session: PublicChallengeSession = {
    session: String(res?.session ?? ''),
    memberName: String(res?.member_name ?? params.name),
    expiresAt: String(res?.expires_at ?? ''),
  };
  try {
    window.localStorage.setItem(`${SESSION_KEY_PREFIX}${params.token}`, JSON.stringify(session));
  } catch {
    /* ignore */
  }
  return { session, statuses: mapStatuses(res?.statuses) };
}

export async function fetchMyChallengeStatuses(session: string): Promise<PublicChallengeStatus[]> {
  const res = await postAnonRpc<Record<string, unknown>>('challenge_public_my_status', { p_session: session });
  return mapStatuses(res?.statuses);
}

export async function loadMyChallengeSubmission(
  session: string,
  itemId: string
): Promise<{ answers: ChallengeAnswer[]; submittedAt: string; locked: boolean } | null> {
  const res = await postAnonRpc<Record<string, unknown>>('challenge_public_load_submission', {
    p_session: session,
    p_item_id: itemId,
  });
  if (res?.found !== true) return null;
  const answers = (Array.isArray(res.answers) ? res.answers : []).map((a) => {
    const o = (a ?? {}) as Record<string, unknown>;
    return {
      missionId: String(o.missionId ?? ''),
      text: typeof o.text === 'string' ? o.text : '',
      photoPath: typeof o.photoPath === 'string' && o.photoPath ? o.photoPath : null,
    } satisfies ChallengeAnswer;
  });
  return { answers, submittedAt: String(res.submitted_at ?? ''), locked: res.locked === true };
}

/** RPC가 발급한 열람 허가(10분) 경로만 내려받기 가능 */
export async function downloadMyChallengePhoto(path: string): Promise<Blob | null> {
  const { data, error } = await supabase.storage.from(PHOTO_BUCKET).download(path);
  if (error || !data) return null;
  return data;
}

/** 일회용 경로 발급 → 업로드. 반환 경로를 제출 답변에 넣는다 */
export async function uploadChallengePhoto(params: {
  session: string;
  itemId: string;
  missionId: string;
  blob: Blob;
}): Promise<string> {
  const res = await postAnonRpc<Record<string, unknown>>('challenge_public_begin_upload', {
    p_session: params.session,
    p_item_id: params.itemId,
    p_mission_id: params.missionId,
    p_bytes: params.blob.size,
    p_mime: params.blob.type === 'image/jpeg' ? 'image/jpeg' : 'image/webp',
  });
  const path = String(res?.path ?? '');
  if (!path) throw new Error('업로드 경로를 받지 못했습니다.');
  const { error } = await supabase.storage.from(PHOTO_BUCKET).upload(path, params.blob, {
    contentType: params.blob.type === 'image/jpeg' ? 'image/jpeg' : 'image/webp',
    cacheControl: '604800',
    upsert: false,
  });
  if (error) throw new Error(formatChallengePublicError(error.message));
  const thumb = await createChallengePhotoThumb(params.blob);
  if (thumb) {
    await supabase.storage
      .from(PHOTO_BUCKET)
      .upload(challengePhotoThumbPath(path), thumb, { contentType: thumb.type, cacheControl: '604800', upsert: false })
      .catch(() => undefined);
  }
  return path;
}

export async function submitChallenge(params: {
  session: string;
  itemId: string;
  requestId: string;
  answers: ChallengeAnswer[];
}): Promise<{ submittedAt: string }> {
  const res = await postAnonRpc<Record<string, unknown>>('challenge_public_submit', {
    p_session: params.session,
    p_item_id: params.itemId,
    p_request_id: params.requestId,
    p_answers: params.answers,
  });
  return { submittedAt: String(res?.submitted_at ?? '') };
}

// ───────────────────────── 기기 임시 저장(IndexedDB) ─────────────────────────

const DRAFT_DB = 'cp-challenge-drafts';
const DRAFT_STORE = 'drafts';
const DRAFT_VERSION = 1;

export type ChallengeDraft = {
  version: number;
  itemRevision: number;
  answers: Record<string, { text: string; photo?: Blob | null; photoPath?: string | null }>;
  savedAt: string;
};

function openDraftDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null);
      return;
    }
    const req = indexedDB.open(DRAFT_DB, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(DRAFT_STORE)) req.result.createObjectStore(DRAFT_STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
  });
}

function draftKey(token: string, memberName: string, itemId: string): string {
  return `${token}:${memberName.trim().toLowerCase()}:${itemId}`;
}

export async function readChallengeDraft(
  token: string,
  memberName: string,
  itemId: string
): Promise<ChallengeDraft | null> {
  const db = await openDraftDb();
  if (!db) return null;
  return new Promise((resolve) => {
    const tx = db.transaction(DRAFT_STORE, 'readonly');
    const req = tx.objectStore(DRAFT_STORE).get(draftKey(token, memberName, itemId));
    req.onsuccess = () => {
      const raw = req.result as Partial<ChallengeDraft> | undefined;
      if (!raw || typeof raw !== 'object') {
        resolve(null);
        return;
      }
      resolve({
        version: Number(raw.version) || 0,
        itemRevision: Number(raw.itemRevision) || 0,
        answers: raw.answers && typeof raw.answers === 'object' ? raw.answers : {},
        savedAt: String(raw.savedAt ?? ''),
      });
    };
    req.onerror = () => resolve(null);
  });
}

export async function writeChallengeDraft(
  token: string,
  memberName: string,
  itemId: string,
  draft: Omit<ChallengeDraft, 'version' | 'savedAt'>
): Promise<void> {
  const db = await openDraftDb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    const tx = db.transaction(DRAFT_STORE, 'readwrite');
    tx.objectStore(DRAFT_STORE).put(
      { ...draft, version: DRAFT_VERSION, savedAt: new Date().toISOString() },
      draftKey(token, memberName, itemId)
    );
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
  });
}

export async function deleteChallengeDraft(token: string, memberName: string, itemId: string): Promise<void> {
  const db = await openDraftDb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    const tx = db.transaction(DRAFT_STORE, 'readwrite');
    tx.objectStore(DRAFT_STORE).delete(draftKey(token, memberName, itemId));
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
  });
}
